// src/services/soundService.ts

/**
 * Sound Service - إدارة الأصوات والإشعارات الصوتية
 */

export enum SoundType {
  NEW_ORDER = 'new-order',
  ORDER_UPDATED = 'order-updated',
  ORDER_CANCELLED = 'order-cancelled',
  URGENT = 'urgent',
}

interface SoundSettings {
  enabled: boolean;
  volume: number; // 0-1
  repeat: number; // عدد التكرارات
}

class SoundService {
  private sounds: Map<SoundType, HTMLAudioElement>;
  private settings: SoundSettings;
  private readonly STORAGE_KEY = 'order_sound_settings';

  private playbackQueue: { type: SoundType; repeatCount: number; resolve: () => void }[] = [];
  private isProcessingQueue = false;

  constructor() {
    this.sounds = new Map();
    this.settings = this.loadSettings();
    this.initializeSounds();
  }

  /**
   * تحميل الإعدادات من localStorage
   */
  private loadSettings(): SoundSettings {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading sound settings:', error);
    }

    // الإعدادات الافتراضية
    return {
      enabled: true,
      volume: 0.7,
      repeat: 2,
    };
  }

  /**
   * حفظ الإعدادات في localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Error saving sound settings:', error);
    }
  }

  /**
   * تهيئة الأصوات
   */
  private initializeSounds(): void {
    // يمكنك استخدام أصوات مخصصة أو أصوات النظام
    const soundFiles: Record<SoundType, string> = {
      [SoundType.NEW_ORDER]: '/sounds/new-order.mp3',
      [SoundType.ORDER_UPDATED]: '/sounds/new-order.mp3',
      [SoundType.ORDER_CANCELLED]: '/sounds/new-order.mp3',
      [SoundType.URGENT]: '/sounds/new-order.mp3',
    };

    Object.entries(soundFiles).forEach(([type, url]) => {
      const audio = new Audio(url);
      audio.volume = this.settings.volume;
      this.sounds.set(type as SoundType, audio);
    });
  }

  /**
   * معالجة طابور التشغيل للأصوات المتداخلة
   */
  private async processQueue() {
    if (this.isProcessingQueue || this.playbackQueue.length === 0) return;

    this.isProcessingQueue = true;

    while (this.playbackQueue.length > 0) {
      const { type, repeatCount, resolve: resolveItem } = this.playbackQueue.shift()!;
      const audio = this.sounds.get(type);

      if (!audio) {
        console.warn(`Sound type ${type} not found`);
        resolveItem();
        continue;
      }

      try {
        for (let i = 0; i < repeatCount; i++) {
          audio.currentTime = 0;
          await audio.play();

          await new Promise<void>(resolve => {
            audio.onended = () => resolve();
          });

          // توقف قصير بين التكرارات
          if (i < repeatCount - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      } catch (error) {
        if ((error as Error).name !== 'NotAllowedError') {
          console.error('Error playing sound:', error);
        }
      }

      resolveItem(); // Finish this notification promise

      // توقف بسيط بين الإشعارات المختلفة في الطابور
      if (this.playbackQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * تشغيل صوت
   */
  async playSound(type: SoundType, repeat?: number): Promise<void> {
    if (!this.settings.enabled) return;

    const repeatCount = repeat ?? this.settings.repeat;

    return new Promise<void>((resolve) => {
      this.playbackQueue.push({ type, repeatCount, resolve });
      this.processQueue();
    });
  }

  /**
   * تشغيل صوت طلب جديد
   */
  playNewOrderSound(): Promise<void> {
    return this.playSound(SoundType.NEW_ORDER);
  }

  /**
   * تشغيل صوت تحديث الطلب
   */
  playOrderUpdatedSound(): Promise<void> {
    return this.playSound(SoundType.ORDER_UPDATED, 1);
  }

  /**
   * تشغيل صوت إلغاء الطلب
   */
  playOrderCancelledSound(): Promise<void> {
    return this.playSound(SoundType.ORDER_CANCELLED, 1);
  }

  /**
   * تشغيل صوت عاجل
   */
  playUrgentSound(): Promise<void> {
    return this.playSound(SoundType.URGENT, 3);
  }

  /**
   * تفعيل/إيقاف الصوت
   */
  setEnabled(enabled: boolean): void {
    this.settings.enabled = enabled;
    this.saveSettings();
  }

  /**
   * تغيير مستوى الصوت
   */
  setVolume(volume: number): void {
    // التأكد من أن القيمة بين 0 و 1
    this.settings.volume = Math.max(0, Math.min(1, volume));

    // تحديث جميع الأصوات
    this.sounds.forEach(audio => {
      audio.volume = this.settings.volume;
    });

    this.saveSettings();
  }

  /**
   * تغيير عدد التكرارات
   */
  setRepeat(repeat: number): void {
    this.settings.repeat = Math.max(1, Math.min(5, repeat));
    this.saveSettings();
  }

  /**
   * الحصول على الإعدادات الحالية
   */
  getSettings(): SoundSettings {
    return { ...this.settings };
  }

  /**
   * اختبار الصوت
   */
  async testSound(): Promise<void> {
    await this.playSound(SoundType.NEW_ORDER);
  }

  /**
   * إعادة تعيين الإعدادات للافتراضية
   */
  resetSettings(): void {
    this.settings = {
      enabled: true,
      volume: 0.7,
      repeat: 2,
    };

    // تحديث مستوى الصوت لجميع الأصوات
    this.sounds.forEach(audio => {
      audio.volume = this.settings.volume;
    });

    this.saveSettings();
  }
}

// Singleton Instance
export const soundService = new SoundService();

export default soundService;